import { useEffect, type FC } from 'react';
import { createPortal } from 'react-dom';
import { toast, Toaster, useSonner, type ToasterProps } from 'sonner';
import { CircleAlertIcon } from 'src/components/media-and-icons/icons/circle-alert';
import { CircleCheckBigIcon } from 'src/components/media-and-icons/icons/circle-check-big';
import { CircleXIcon } from 'src/components/media-and-icons/icons/circle-x';
import { InfoIcon } from 'src/components/media-and-icons/icons/info';
import { cn } from 'src/utils/cn';

const isTestRunner = window.navigator.userAgent.match(/StorybookTestRunner/);

export const sonnerConfig: ToasterProps = {
  expand: true,
  duration: 5000,
  position: 'top-right',
  closeButton: true,
  richColors: false,
  containerAriaLabel: 'Notifications',
  toastOptions: {
    unstyled: true,
    classNames: {
      toast: cn(
        `
          relative flex gap-lg rounded-xs border-l-6
          bg-background-surface-default px-xl py-lg shadow-elevation-sm
          md:w-[340px]
        `,
        // Disable transitions for the toasts when in test runner mode in order to have stable image regression tests
        isTestRunner && 'transition-none!'
      ),
      content: cn('mr-2xl flex flex-col gap-xs'),
      title: cn(
        `
          flex min-h-2xl flex-col justify-center text-body-base
          [&:has(+[data-description])]:font-body-semibold
        `
      ),
      description: cn('text-body-base text-foreground-surface-subdued'),
      closeButton: cn(
        `
          absolute top-md right-xl min-h-[28px] cursor-pointer
          text-foreground-surface-subdued
          hover:text-foreground-surface-default
          [&>svg]:size-xl
        `
      ),
      info: cn('border-border-support-info-default'),
      success: cn('border-border-support-success-default'),
      warning: cn('border-border-support-warning-default'),
      error: cn('border-border-support-error-default'),
    },
  },
  icons: {
    info: <InfoIcon size="md" className="text-foreground-support-info" />,
    success: (
      <CircleCheckBigIcon
        size="md"
        className="text-foreground-support-success"
      />
    ),
    warning: (
      <CircleAlertIcon size="md" className="text-foreground-support-warning" />
    ),
    error: <CircleXIcon size="md" className="text-foreground-support-error" />,
  },
};

export const useSonnerErrorDuration = () => {
  const { toasts } = useSonner();

  useEffect(() => {
    toasts.forEach((toastT) => {
      if (toastT.type === 'error' && !toastT.duration) {
        toast.error(toastT.title, { id: toastT.id, duration: Infinity });
      }
    });
  }, [toasts]);
};

export const sonnerContainerId = 'sonner-container';

export const RootToaster: FC<ToasterProps> = (props?) => {
  return createPortal(
    <div id={sonnerContainerId}>
      <Toaster {...{ ...sonnerConfig, ...props }} />
    </div>,
    document.body
  );
};
